import { find } from 'lodash';

import { InterestsSetType } from '../../components/InterestsSetEditor/InterestsSetEditor';
import { castStringToEnum } from '../../service/CastHelpers';
import {
    AudienceTargetingsType,
    AudienceTargetingsValue
} from '../../typings/audienceTargetings';
import {
    CryptaSegment,
    CryptaSegmentsByType
} from '../../typings/cryptaSegments';
import {
    CryptaInterestTypeWeb,
    CryptaInterestTypeWebInput,
    GdGoalType,
    GdRetargetingConditionRuleItemReqInput,
    GdRetargetingConditionRuleTypeInput,
    GdUpdateCpmRetargetingConditionItemInput,
    RetargetingConditionsQueryForUserProfile_client_retargetingConditions_rowset,
    RetargetingConditionsQueryForUserProfile_client_retargetingConditions_rowset_conditionRules,
    RetargetingConditionsQueryForUserProfile_client_retargetingConditions_rowset_conditionRules_goals,
    WebGoalType
} from '../../typings/graphql-grid';
import { RetargetingConditionDataRulesGroup } from '../../typings/retargeting';
import {
    UserProfileRetargetingCondition,
    UserProfileRetargetingConditionGoalType,
    UserProfileRetargetingConditionRule,
    UserProfileRetargetingConditionRuleGoal,
    UserProfileRetargetingConditionRuleType
} from '../../typings/userProfileRetargeting';
import {
    mapRetargetingConditionRulesGroupsFromApiFormat,
    mapRulesGroupsToApiFormat
} from '../retargeting/retargeting';

type ConditionRule = RetargetingConditionsQueryForUserProfile_client_retargetingConditions_rowset_conditionRules;
type ConditionRulesByType = Record<GdGoalType, ConditionRule[]>;

export const mapUserProfileRetargetingConditionFromApiFormat = (
    item: RetargetingConditionsQueryForUserProfile_client_retargetingConditions_rowset
): UserProfileRetargetingCondition => {
    return {
        id: String(item.retargetingConditionId),
        name: item.name,
        conditionRules: item.conditionRules.map(
            mapUserProfileRetargetingConditionRuleFromApiFormat
        ),
        audienceTargetings: splitRetargetingConditionToAudienceTargetings(
            item.conditionRules
        )
    };
};

export const splitRulesByType = (
    conditionRules: ConditionRule[]
): ConditionRulesByType => {
    return conditionRules.reduce(
        (
            result: ConditionRulesByType,
            rule: ConditionRule
        ): ConditionRulesByType => {
            const type = rule.goals[0].type;

            if (!result[type]) {
                result[type] = [];
            }

            result[type].push(rule);

            return result;
        },
        {} as ConditionRulesByType
    );
};

const getMetrikaOrAudienceRuls = (
    rulesByType: ConditionRulesByType
): ConditionRule[] => {
    return [
        GdGoalType.GOAL,
        GdGoalType.ECOMMERCE,
        GdGoalType.SEGMENT,
        GdGoalType.AUDIENCE
    ].reduce(
        (result: ConditionRule[], type: GdGoalType) =>
            result.concat(rulesByType[type] || []),
        []
    );
};

export const splitRetargetingConditionToAudienceTargetings = (
    conditionRules: ConditionRule[]
): AudienceTargetingsValue => {
    const audienceTargetings = {};
    const rulesByType = splitRulesByType(conditionRules);
    const metrikaOrAudienceRuls = getMetrikaOrAudienceRuls(rulesByType);
    const interestsRuls =
        rulesByType[UserProfileRetargetingConditionGoalType.INTERESTS];
    const socialDemoRuls =
        rulesByType[UserProfileRetargetingConditionGoalType.SOCIAL_DEMO];
    const behaviorsRuls =
        rulesByType[UserProfileRetargetingConditionGoalType.BEHAVIORS];
    const familyRuls =
        rulesByType[UserProfileRetargetingConditionGoalType.FAMILY];
    const audioGenres =
        rulesByType[UserProfileRetargetingConditionGoalType.AUDIO_GENRES];

    if (metrikaOrAudienceRuls.length) {
        audienceTargetings[AudienceTargetingsType.audienceMetrics] = {
            rulesGroups: mapRetargetingConditionRulesGroupsFromApiFormat(
                metrikaOrAudienceRuls
            ).map((rule: RetargetingConditionDataRulesGroup) => {
                return {
                    ...rule,
                    items: rule.items.map(goal => ({
                        ...goal,
                        id: Number(goal.id)
                    }))
                };
            })
        };
    }

    if (interestsRuls) {
        audienceTargetings[
            AudienceTargetingsType.interests
        ] = interestsRuls.map((rule: ConditionRule) => ({
            period: rule.interestType,
            checkedList: rule.goals.map(goal => String(goal.id)),
            openedList: []
        }));
    }

    if (socialDemoRuls) {
        audienceTargetings[
            AudienceTargetingsType.socialDemo
        ] = getFlattenAudienceTargeting(socialDemoRuls);
    }

    if (behaviorsRuls) {
        audienceTargetings[
            AudienceTargetingsType.behaviors
        ] = getFlattenAudienceTargeting(behaviorsRuls);
    }

    if (familyRuls) {
        audienceTargetings[
            AudienceTargetingsType.family
        ] = getFlattenAudienceTargeting(familyRuls);
    }

    if (audioGenres) {
        audienceTargetings[
            AudienceTargetingsType.audioGenres
        ] = getFlattenAudienceTargeting(audioGenres);
    }

    return audienceTargetings;
};

const getFlattenAudienceTargeting = (ruls: ConditionRule[]): string[] => {
    return ruls.reduce((result: string[], rule: ConditionRule) => {
        return result.concat(rule.goals.map(goal => String(goal.id)));
    }, []);
};

const mapUserProfileRetargetingConditionRuleFromApiFormat = (
    rule: ConditionRule
): UserProfileRetargetingConditionRule => {
    return {
        interestType: rule.interestType
            ? castStringToEnum(rule.interestType, CryptaInterestTypeWeb)
            : undefined,
        goals: rule.goals.map(
            mapUserProfileRetargetingConditionGoalFromApiFormat
        ),
        type: castStringToEnum(
            rule.type,
            UserProfileRetargetingConditionRuleType
        )
    };
};

const mapUserProfileRetargetingConditionGoalFromApiFormat = (
    goal: RetargetingConditionsQueryForUserProfile_client_retargetingConditions_rowset_conditionRules_goals
): UserProfileRetargetingConditionRuleGoal => {
    return {
        id: String(goal.id),
        time: Number(goal.time || 0),
        type: castStringToEnum(
            goal.type,
            UserProfileRetargetingConditionGoalType
        )
    };
};

function mapAudienceTargetingsToRulesApiFormat(
    audienceTargetings: AudienceTargetingsValue,
    cryptaSegmentsByType: CryptaSegmentsByType
): GdRetargetingConditionRuleItemReqInput[] {
    const socialDemo = audienceTargetings[AudienceTargetingsType.socialDemo];
    const behaviors = audienceTargetings[AudienceTargetingsType.behaviors];
    const interests = audienceTargetings[AudienceTargetingsType.interests];
    const family = audienceTargetings[AudienceTargetingsType.family];
    const audioGenres = audienceTargetings[AudienceTargetingsType.audioGenres];
    const audienceMetrics =
        audienceTargetings[AudienceTargetingsType.audienceMetrics];
    let rules: GdRetargetingConditionRuleItemReqInput[] = [];

    if (audienceMetrics && audienceMetrics.rulesGroups) {
        rules = rules.concat(
            mapRulesGroupsToApiFormat(audienceMetrics.rulesGroups)
        );
    }

    if (socialDemo && socialDemo.length) {
        rules = rules.concat(
            mapAudienceTargetingByParent(
                cryptaSegmentsByType[WebGoalType.social_demo],
                socialDemo
            )
        );
    }

    if (behaviors && behaviors.length) {
        rules = rules.concat(
            mapAudienceTargetingByParent(
                cryptaSegmentsByType[WebGoalType.behaviors],
                behaviors
            )
        );
    }

    if (family && family.length) {
        rules = rules.concat(
            mapAudienceTargetingByParent(
                cryptaSegmentsByType[WebGoalType.family],
                family
            )
        );
    }

    if (audioGenres && audioGenres.length) {
        rules = rules.concat(
            mapAudienceTargetingByParent(
                cryptaSegmentsByType[WebGoalType.audio_genres],
                audioGenres
            )
        );
    }

    if (interests && interests.length) {
        rules = rules.concat(
            interests
                .filter(set => set.checkedList.length)
                .map((set: InterestsSetType) => ({
                    interestType: castStringToEnum(
                        set.period,
                        CryptaInterestTypeWebInput
                    ),
                    type: GdRetargetingConditionRuleTypeInput.OR,
                    goals: set.checkedList.map((id: string) => ({
                        id: id,
                        time: 0
                    }))
                }))
        );
    }

    return rules;
}

function mapAudienceTargetingByParent(
    cryptaSegments: CryptaSegment[],
    ids: string[]
): GdRetargetingConditionRuleItemReqInput[] {
    const groupByParent: { [id: number]: string[] } = {};

    ids.forEach(id => {
        const item = find(cryptaSegments, { id });

        if (item && item.parentId) {
            if (!groupByParent[item.parentId]) {
                groupByParent[item.parentId] = [];
            }

            groupByParent[item.parentId].push(id);
        }
    });

    return Object.keys(groupByParent).map(parentId => ({
        type: GdRetargetingConditionRuleTypeInput.OR,
        goals: groupByParent[parentId].map((id: string) => ({
            id: id,
            time: 0
        }))
    }));
}

export const mapAudienceTargetingsToRetargetingConditionsApiFormat = ({
    audienceTargetings,
    cryptaSegmentsByType,
    audienceTargetingsTemplateName
}: {
    audienceTargetings: AudienceTargetingsValue;
    cryptaSegmentsByType: CryptaSegmentsByType;
    audienceTargetingsTemplateName: string;
}): GdUpdateCpmRetargetingConditionItemInput => {
    return {
        name: audienceTargetingsTemplateName,
        conditionRules: mapAudienceTargetingsToRulesApiFormat(
            audienceTargetings,
            cryptaSegmentsByType
        )
    };
};
